import React, { useState } from 'react';
import {
  Card,
  Typography,
  Input,
  Switch,
  Button,
  Divider,
  Collapse,
  Alert,
  Space,
  Tag,
} from 'antd';
import { normalizeBase } from '@digiwedge/ui-health-panels';
import { useGlobalStore } from '@digiwedge/global-state';

export type IdpStatus = 'idle' | 'ok' | 'fail' | 'mfa';

type DiagResult = {
  status: number;
  ok: boolean;
  audience: string;
  endpoint: string;
  body: unknown;
};

export default function IdpDiagnosticsPanel({
  idpBase,
  audience,
  onStatusChange,
}: {
  idpBase: string;
  audience: string;
  onStatusChange?: (s: IdpStatus) => void;
}): React.ReactElement {
  const { Text } = Typography;
  const [jsonView, setJsonView] = useState<'raw' | 'tree'>('raw');
  const [diagParsed, setDiagParsed] = useState<DiagResult | null>(null);
  const [lastMessage, setLastMessage] = useState<string | null>(null);
  const [statusChip, setStatusChip] = useState<IdpStatus>('idle');

  const updateStatus = (s: IdpStatus) => {
    setStatusChip(s);
    try {
      onStatusChange?.(s);
    } catch {
      /* ignore */
    }
  };

  const readXsrf = () => {
    try {
      const cookie = typeof document !== 'undefined' ? document.cookie : '';
      const hit = cookie
        .split(';')
        .map((s) => s.trim())
        .find((p) => p.startsWith('XSRF-TOKEN='));
      if (!hit) return undefined;
      return decodeURIComponent(hit.split('=')[1] || '');
    } catch {
      return undefined;
    }
  };
  const [xsrf, setXsrf] = useState<string | undefined>(() => readXsrf());

  const effectiveAudience = (() => {
    try {
      const meta = (document.querySelector('meta[name="idp-audience"]') as HTMLMetaElement | null)
        ?.content;
      return (meta || audience || '').trim();
    } catch {
      return audience;
    }
  })();

  const TitleWithChip = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span>Identity Provider Diagnostics</span>
      <Tag
        color={
          statusChip === 'ok'
            ? 'green'
            : statusChip === 'mfa'
              ? 'gold'
              : statusChip === 'fail'
                ? 'red'
                : 'default'
        }
      >
        {statusChip === 'idle' ? 'IDLE' : statusChip.toUpperCase()}
      </Tag>
    </div>
  );

  return (
    <Card title={TitleWithChip} style={{ marginTop: 16 }}>
      <Text type="secondary">
        Validate the CMP portal audience against the IDP. Generate CSRF cookies and run a direct
        credential login for debugging. Tokens are applied to the local store on success.
      </Text>
      <Divider />
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <Text strong>Base:</Text>
        <Text code>{normalizeBase(idpBase) || 'not set'}</Text>
        <Text strong>Audience:</Text>
        <Text code>{effectiveAudience || 'not set'}</Text>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
        <Input style={{ maxWidth: 320 }} placeholder="Email" type="email" id="diag-email" />
        <Input.Password style={{ maxWidth: 240 }} placeholder="Password" id="diag-password" />
        <Switch id="diag-remember" />
        <Text type="secondary">Remember</Text>
        <Tag color={xsrf ? 'green' : 'default'}>CSRF cookie: {xsrf ? 'present' : 'missing'}</Tag>
        <Button
          size="small"
          onClick={async () => {
            try {
              const base = normalizeBase(idpBase);
              if (base) await fetch(`${base}/api/auth/csrf`, { credentials: 'include' });
              setTimeout(() => setXsrf(readXsrf()), 0);
              // fetching CSRF does not change overall auth status
            } catch {
              /* ignore */
            }
          }}
        >
          Ensure CSRF cookie
        </Button>
        <Button
          type="primary"
          onClick={async () => {
            const emailVal =
              (document.getElementById('diag-email') as HTMLInputElement | null)?.value?.trim() ||
              '';
            const passVal =
              (document.getElementById('diag-password') as HTMLInputElement | null)?.value || '';
            const rememberVal = Boolean(
              (document.getElementById('diag-remember') as HTMLInputElement | null)?.checked,
            );
            const base = normalizeBase(idpBase);
            let status = 0;
            let ok = false;
            let body: unknown = null;
            try {
              const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-Client-Audience': effectiveAudience,
              };
              const xsrfVal = readXsrf();
              if (xsrfVal) headers['X-XSRF-Token'] = xsrfVal;
              const res = await fetch(`${base}/api/auth/login`, {
                method: 'POST',
                credentials: 'include',
                headers,
                body: JSON.stringify({ email: emailVal, password: passVal, remember: rememberVal }),
              });
              status = res.status;
              ok = res.ok;
              const text = await res.text();
              try {
                body = text ? JSON.parse(text) : null;
              } catch {
                body = { raw: text };
              }
            } catch (e) {
              body = { error: String(e) };
            }
            const pretty = JSON.stringify(
              {
                status,
                ok,
                audience: effectiveAudience,
                endpoint: `${base}/api/auth/login`,
                body,
              },
              null,
              2,
            );
            const out = document.getElementById('diag-output') as HTMLTextAreaElement | null;
            if (out) out.value = pretty;
            setDiagParsed({
              status,
              ok,
              audience: effectiveAudience,
              endpoint: `${base}/api/auth/login`,
              body,
            });
            // Highlight server message if present
            let msg: string | null = null;
            if (body && typeof body === 'object') {
              const rec = body as Record<string, unknown>;
              if (typeof rec.message === 'string' && rec.message.trim()) {
                msg = rec.message;
              } else if (typeof rec.error === 'string' && rec.error.trim()) {
                msg = rec.error;
              }
              // Apply tokens on success
              const at =
                typeof rec['accessToken'] === 'string' ? (rec['accessToken'] as string) : undefined;
              const rt =
                typeof rec['refreshToken'] === 'string'
                  ? (rec['refreshToken'] as string)
                  : undefined;
              const j = typeof rec['jti'] === 'string' ? (rec['jti'] as string) : undefined;
              if (ok && at) {
                useGlobalStore.getState().login(at, rt, j, rememberVal);
                msg = 'Login successful — tokens applied';
                updateStatus('ok');
              } else if ((rec['mfaRequired'] as boolean) === true) {
                msg = 'MFA required — complete MFA flow to proceed';
                updateStatus('mfa');
              } else {
                updateStatus('fail');
              }
            }
            setLastMessage(msg);
            setXsrf(readXsrf());
          }}
        >
          Run login
        </Button>
      </div>

      {lastMessage && (
        <div style={{ marginTop: 12 }}>
          <Alert
            type="info"
            showIcon
            message={lastMessage}
            description={diagParsed ? `Status: ${diagParsed.status}` : undefined}
          />
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <Collapse
          size="small"
          items={[
            {
              key: 'raw-json',
              label: 'Raw JSON (click to expand)',
              children: (
                <div>
                  <Space>
                    <Button size="small" onClick={() => setJsonView('raw')}>
                      Raw
                    </Button>
                    <Button size="small" onClick={() => setJsonView('tree')}>
                      Tree
                    </Button>
                  </Space>
                  {jsonView === 'raw' ? (
                    <textarea id="diag-output" style={{ width: '100%', minHeight: 240 }} readOnly />
                  ) : diagParsed ? (
                    <div
                      style={{
                        maxHeight: 360,
                        overflow: 'auto',
                        border: '1px solid #f0f0f0',
                        borderRadius: 6,
                        padding: 8,
                        marginTop: 8,
                      }}
                    >
                      <pre style={{ margin: 0 }}>{JSON.stringify(diagParsed, null, 2)}</pre>
                    </div>
                  ) : (
                    <Alert type="info" message="Run login to populate JSON view" />
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>
    </Card>
  );
}
